import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { IDraftVoice } from "@/types/common";

interface iCloneVoiceFormContextType {
  formData: IDraftVoice;
  updateFormData: (formDataParams: IDraftVoice) => void;
  referenceValues: Record<string, unknown>;
  setReferenceValues: (values: Record<string, unknown>) => void;
  resetForm: () => void;
  hasData: boolean;
}

const CloneVoiceFormContext = createContext<iCloneVoiceFormContextType | undefined>(undefined);

interface CloneVoiceFormProviderProps {
  children: ReactNode;
  initialData?: Partial<IDraftVoice>;
}

export function CloneVoiceFormProvider({ children, initialData = {} }: CloneVoiceFormProviderProps) {
  const [stateFormData, setStateFormData] = useState<Partial<IDraftVoice>>(initialData);
  const [stateReferenceValues, setStateReferenceValuesState] = useState<Record<string, unknown>>({});

  const updateFormData = useCallback((formDataParams: Partial<IDraftVoice>) => {
    setStateFormData((prev: Partial<IDraftVoice>) => ({
      ...prev,
      ...formDataParams,
    }));
  }, []);

  const setReferenceValues = useCallback((values: Record<string, unknown>) => {
    setStateReferenceValuesState(values);
  }, []);

  const resetForm = useCallback(() => {
    setStateFormData({});
    setStateReferenceValuesState({});
  }, []);

  const hasData = Object.keys(stateFormData).length > 0;

  const providerValues = useMemo(
    () => ({
      formData: stateFormData,
      updateFormData,
      referenceValues: stateReferenceValues,
      setReferenceValues,
      resetForm,
      hasData,
    }),
    [stateFormData, hasData, stateReferenceValues, resetForm, setReferenceValues, updateFormData],
  );

  return <CloneVoiceFormContext.Provider value={providerValues}>{children}</CloneVoiceFormContext.Provider>;
}

export function useCloneVoiceForm() {
  const context = useContext(CloneVoiceFormContext);
  if (context === undefined) {
    throw new Error("useCloneVoiceForm must be used within a CloneVoiceFormProvider");
  }
  return context;
}
